// list from https://documentation.identity.entrust.com/guide/supported-applicant-countries/
//
// `alpha3` is the value every consumer keys on (form payloads, billing-package lookups).
// `alpha2` exists only so a country can be *rendered* in the user's language: it is the code
// `Intl.DisplayNames({ type: 'region' })` understands (see hooks/useCountryNames.ts), and the
// CLDR data behind it is what supplies the French names — so no 250-key translation file is
// needed. Codes are ISO 3166-1 alpha-2, except Kosovo, whose non-ISO `RKS` alpha-3 pairs with
// the de-facto `XK` that CLDR recognises.
const countries = [
  { name: 'Afghanistan', alpha2: 'AF', alpha3: 'AFG', region: 'Asia', subregion: 'Southern Asia' },
  { name: 'Albania', alpha2: 'AL', alpha3: 'ALB', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Algeria', alpha2: 'DZ', alpha3: 'DZA', region: 'Africa', subregion: 'Northern Africa' },
  {
    name: 'American Samoa',
    alpha2: 'AS',
    alpha3: 'ASM',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  { name: 'Andorra', alpha2: 'AD', alpha3: 'AND', region: 'Europe', subregion: 'Southern Europe' },
  {
    name: 'Angola',
    alpha2: 'AO',
    alpha3: 'AGO',
    region: 'Africa',
    subregion: 'Middle Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Anguilla',
    alpha2: 'AI',
    alpha3: 'AIA',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  { name: 'Antarctica', alpha2: 'AQ', alpha3: 'ATA', region: '', subregion: '', supportedIdentityReport: false },
  {
    name: 'Antigua and Barbuda',
    alpha2: 'AG',
    alpha3: 'ATG',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  { name: 'Argentina', alpha2: 'AR', alpha3: 'ARG', region: 'Americas', subregion: 'South America' },
  { name: 'Armenia', alpha2: 'AM', alpha3: 'ARM', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Aruba', alpha2: 'AW', alpha3: 'ABW', region: 'Americas', subregion: 'Caribbean' },
  {
    name: 'Australia',
    alpha2: 'AU',
    alpha3: 'AUS',
    region: 'Oceania',
    subregion: 'Australia and New Zealand',
  },
  { name: 'Austria', alpha2: 'AT', alpha3: 'AUT', region: 'Europe', subregion: 'Western Europe' },
  { name: 'Azerbaijan', alpha2: 'AZ', alpha3: 'AZE', region: 'Asia', subregion: 'Western Asia' },
  {
    name: 'Bahamas',
    alpha2: 'BS',
    alpha3: 'BHS',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  { name: 'Bahrain', alpha2: 'BH', alpha3: 'BHR', region: 'Asia', subregion: 'Western Asia' },
  {
    name: 'Bangladesh',
    alpha2: 'BD',
    alpha3: 'BGD',
    region: 'Asia',
    subregion: 'Southern Asia',
    supportedIdentityReport: false,
  },
  { name: 'Barbados', alpha2: 'BB', alpha3: 'BRB', region: 'Americas', subregion: 'Caribbean' },
  { name: 'Belarus', alpha2: 'BY', alpha3: 'BLR', region: 'Europe', subregion: 'Eastern Europe' },
  { name: 'Belgium', alpha2: 'BE', alpha3: 'BEL', region: 'Europe', subregion: 'Western Europe' },
  { name: 'Belize', alpha2: 'BZ', alpha3: 'BLZ', region: 'Americas', subregion: 'Central America' },
  { name: 'Benin', alpha2: 'BJ', alpha3: 'BEN', region: 'Africa', subregion: 'Western Africa' },
  { name: 'Bermuda', alpha2: 'BM', alpha3: 'BMU', region: 'Americas', subregion: 'Northern America' },
  { name: 'Bhutan', alpha2: 'BT', alpha3: 'BTN', region: 'Asia', subregion: 'Southern Asia' },
  {
    name: 'Bolivia (Plurinational State of)',
    alpha2: 'BO',
    alpha3: 'BOL',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  {
    name: 'Bonaire, Sint Eustatius and Saba',
    alpha2: 'BQ',
    alpha3: 'BES',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  {
    name: 'Bosnia and Herzegovina',
    alpha2: 'BA',
    alpha3: 'BIH',
    region: 'Europe',
    subregion: 'Southern Europe',
  },
  {
    name: 'Botswana',
    alpha2: 'BW',
    alpha3: 'BWA',
    region: 'Africa',
    subregion: 'Southern Africa',
    supportedIdentityReport: false,
  },
  { name: 'Bouvet Island', alpha2: 'BV', alpha3: 'BVT', region: '', subregion: '', supportedIdentityReport: false },
  { name: 'Brazil', alpha2: 'BR', alpha3: 'BRA', region: 'Americas', subregion: 'South America' },
  {
    name: 'British Indian Ocean Territory',
    alpha2: 'IO',
    alpha3: 'IOT',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Brunei Darussalam',
    alpha2: 'BN',
    alpha3: 'BRN',
    region: 'Asia',
    subregion: 'South-Eastern Asia',
  },
  { name: 'Bulgaria', alpha2: 'BG', alpha3: 'BGR', region: 'Europe', subregion: 'Eastern Europe' },
  { name: 'Burkina Faso', alpha2: 'BF', alpha3: 'BFA', region: 'Africa', subregion: 'Western Africa' },
  { name: 'Burundi', alpha2: 'BI', alpha3: 'BDI', region: 'Africa', subregion: 'Eastern Africa' },
  {
    name: 'Cabo Verde',
    alpha2: 'CV',
    alpha3: 'CPV',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  { name: 'Cambodia', alpha2: 'KH', alpha3: 'KHM', region: 'Asia', subregion: 'South-Eastern Asia' },
  { name: 'Cameroon', alpha2: 'CM', alpha3: 'CMR', region: 'Africa', subregion: 'Middle Africa' },
  { name: 'Canada', alpha2: 'CA', alpha3: 'CAN', region: 'Americas', subregion: 'Northern America' },
  { name: 'Cayman Islands', alpha2: 'KY', alpha3: 'CYM', region: 'Americas', subregion: 'Caribbean' },
  {
    name: 'Central African Republic',
    alpha2: 'CF',
    alpha3: 'CAF',
    region: 'Africa',
    subregion: 'Middle Africa',
  },
  {
    name: 'Chad',
    alpha2: 'TD',
    alpha3: 'TCD',
    region: 'Africa',
    subregion: 'Middle Africa',
    supportedIdentityReport: false,
  },
  { name: 'Chile', alpha2: 'CL', alpha3: 'CHL', region: 'Americas', subregion: 'South America' },
  { name: 'China', alpha2: 'CN', alpha3: 'CHN', region: 'Asia', subregion: 'Eastern Asia' },
  {
    name: 'Christmas Island',
    alpha2: 'CX',
    alpha3: 'CXR',
    region: 'Oceania',
    subregion: 'Australia and New Zealand',
    supportedIdentityReport: false,
  },
  {
    name: 'Cocos (Keeling) Islands',
    alpha2: 'CC',
    alpha3: 'CCK',
    region: 'Oceania',
    subregion: 'Australia and New Zealand',
    supportedIdentityReport: false,
  },
  { name: 'Colombia', alpha2: 'CO', alpha3: 'COL', region: 'Americas', subregion: 'South America' },
  {
    name: 'Comoros',
    alpha2: 'KM',
    alpha3: 'COM',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  { name: 'Congo', alpha2: 'CG', alpha3: 'COG', region: 'Africa', subregion: 'Middle Africa' },
  {
    name: 'Congo (Democratic Republic of the)',
    alpha2: 'CD',
    alpha3: 'COD',
    region: 'Africa',
    subregion: 'Middle Africa',
    supportedIdentityReport: false,
  },
  { name: 'Cook Islands', alpha2: 'CK', alpha3: 'COK', region: 'Oceania', subregion: 'Polynesia' },
  {
    name: 'Costa Rica',
    alpha2: 'CR',
    alpha3: 'CRI',
    region: 'Americas',
    subregion: 'Central America',
  },
  { name: 'Croatia', alpha2: 'HR', alpha3: 'HRV', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Cuba', alpha2: 'CU', alpha3: 'CUB', region: 'Americas', subregion: 'Caribbean' },
  {
    name: 'Curaçao',
    alpha2: 'CW',
    alpha3: 'CUW',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  { name: 'Cyprus', alpha2: 'CY', alpha3: 'CYP', region: 'Asia', subregion: 'Western Asia' },
  {
    name: 'Czech Republic',
    alpha2: 'CZ',
    alpha3: 'CZE',
    region: 'Europe',
    subregion: 'Eastern Europe',
  },
  {
    name: "Côte d'Ivoire",
    alpha2: 'CI',
    alpha3: 'CIV',
    region: 'Africa',
    subregion: 'Western Africa',
  },
  { name: 'Denmark', alpha2: 'DK', alpha3: 'DNK', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Djibouti', alpha2: 'DJ', alpha3: 'DJI', region: 'Africa', subregion: 'Eastern Africa' },
  { name: 'Dominica', alpha2: 'DM', alpha3: 'DMA', region: 'Americas', subregion: 'Caribbean' },
  {
    name: 'Dominican Republic',
    alpha2: 'DO',
    alpha3: 'DOM',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  { name: 'Ecuador', alpha2: 'EC', alpha3: 'ECU', region: 'Americas', subregion: 'South America' },
  { name: 'Egypt', alpha2: 'EG', alpha3: 'EGY', region: 'Africa', subregion: 'Northern Africa' },
  {
    name: 'El Salvador',
    alpha2: 'SV',
    alpha3: 'SLV',
    region: 'Americas',
    subregion: 'Central America',
  },
  {
    name: 'Equatorial Guinea',
    alpha2: 'GQ',
    alpha3: 'GNQ',
    region: 'Africa',
    subregion: 'Middle Africa',
  },
  {
    name: 'Eritrea',
    alpha2: 'ER',
    alpha3: 'ERI',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  { name: 'Estonia', alpha2: 'EE', alpha3: 'EST', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Ethiopia', alpha2: 'ET', alpha3: 'ETH', region: 'Africa', subregion: 'Eastern Africa' },
  {
    name: 'Falkland Islands (Malvinas)',
    alpha2: 'FK',
    alpha3: 'FLK',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  {
    name: 'Faroe Islands',
    alpha2: 'FO',
    alpha3: 'FRO',
    region: 'Europe',
    subregion: 'Northern Europe',
  },
  { name: 'Fiji', alpha2: 'FJ', alpha3: 'FJI', region: 'Oceania', subregion: 'Melanesia' },
  { name: 'Finland', alpha2: 'FI', alpha3: 'FIN', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'France', alpha2: 'FR', alpha3: 'FRA', region: 'Europe', subregion: 'Western Europe' },
  {
    name: 'French Guiana',
    alpha2: 'GF',
    alpha3: 'GUF',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  {
    name: 'French Polynesia',
    alpha2: 'PF',
    alpha3: 'PYF',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  {
    name: 'French Southern Territories',
    alpha2: 'TF',
    alpha3: 'ATF',
    region: '',
    subregion: '',
    supportedIdentityReport: false,
  },
  { name: 'Gabon', alpha2: 'GA', alpha3: 'GAB', region: 'Africa', subregion: 'Middle Africa' },
  {
    name: 'Gambia',
    alpha2: 'GM',
    alpha3: 'GMB',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  { name: 'Georgia', alpha2: 'GE', alpha3: 'GEO', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Germany', alpha2: 'DE', alpha3: 'DEU', region: 'Europe', subregion: 'Western Europe' },
  {
    name: 'Ghana',
    alpha2: 'GH',
    alpha3: 'GHA',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  { name: 'Gibraltar', alpha2: 'GI', alpha3: 'GIB', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Greece', alpha2: 'GR', alpha3: 'GRC', region: 'Europe', subregion: 'Southern Europe' },
  {
    name: 'Greenland',
    alpha2: 'GL',
    alpha3: 'GRL',
    region: 'Americas',
    subregion: 'Northern America',
    supportedIdentityReport: false,
  },
  { name: 'Grenada', alpha2: 'GD', alpha3: 'GRD', region: 'Americas', subregion: 'Caribbean' },
  {
    name: 'Guadeloupe',
    alpha2: 'GP',
    alpha3: 'GLP',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  {
    name: 'Guam',
    alpha2: 'GU',
    alpha3: 'GUM',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  { name: 'Guatemala', alpha2: 'GT', alpha3: 'GTM', region: 'Americas', subregion: 'Central America' },
  { name: 'Guernsey', alpha2: 'GG', alpha3: 'GGY', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Guinea', alpha2: 'GN', alpha3: 'GIN', region: 'Africa', subregion: 'Western Africa' },
  {
    name: 'Guinea-Bissau',
    alpha2: 'GW',
    alpha3: 'GNB',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Guyana',
    alpha2: 'GY',
    alpha3: 'GUY',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  {
    name: 'Haiti',
    alpha2: 'HT',
    alpha3: 'HTI',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  {
    name: 'Heard Island and McDonald Islands',
    alpha2: 'HM',
    alpha3: 'HMD',
    region: '',
    subregion: '',
    supportedIdentityReport: false,
  },
  {
    name: 'Holy See',
    alpha2: 'VA',
    alpha3: 'VAT',
    region: 'Europe',
    subregion: 'Southern Europe',
    supportedIdentityReport: false,
  },
  { name: 'Honduras', alpha2: 'HN', alpha3: 'HND', region: 'Americas', subregion: 'Central America' },
  { name: 'Hong Kong', alpha2: 'HK', alpha3: 'HKG', region: 'Asia', subregion: 'Eastern Asia' },
  { name: 'Hungary', alpha2: 'HU', alpha3: 'HUN', region: 'Europe', subregion: 'Eastern Europe' },
  { name: 'Iceland', alpha2: 'IS', alpha3: 'ISL', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'India', alpha2: 'IN', alpha3: 'IND', region: 'Asia', subregion: 'Southern Asia' },
  { name: 'Indonesia', alpha2: 'ID', alpha3: 'IDN', region: 'Asia', subregion: 'South-Eastern Asia' },
  {
    name: 'Iran (Islamic Republic of)',
    alpha2: 'IR',
    alpha3: 'IRN',
    region: 'Asia',
    subregion: 'Southern Asia',
  },
  {
    name: 'Iraq',
    alpha2: 'IQ',
    alpha3: 'IRQ',
    region: 'Asia',
    subregion: 'Western Asia',
    supportedIdentityReport: false,
  },
  { name: 'Ireland', alpha2: 'IE', alpha3: 'IRL', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Isle of Man', alpha2: 'IM', alpha3: 'IMN', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Israel', alpha2: 'IL', alpha3: 'ISR', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Italy', alpha2: 'IT', alpha3: 'ITA', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Jamaica', alpha2: 'JM', alpha3: 'JAM', region: 'Americas', subregion: 'Caribbean' },
  { name: 'Japan', alpha2: 'JP', alpha3: 'JPN', region: 'Asia', subregion: 'Eastern Asia' },
  { name: 'Jersey', alpha2: 'JE', alpha3: 'JEY', region: 'Europe', subregion: 'Northern Europe' },
  {
    name: 'Jordan',
    alpha2: 'JO',
    alpha3: 'JOR',
    region: 'Asia',
    subregion: 'Western Asia',
    supportedIdentityReport: false,
  },
  { name: 'Kazakhstan', alpha2: 'KZ', alpha3: 'KAZ', region: 'Asia', subregion: 'Central Asia' },
  { name: 'Kenya', alpha2: 'KE', alpha3: 'KEN', region: 'Africa', subregion: 'Eastern Africa' },
  {
    name: 'Kiribati',
    alpha2: 'KI',
    alpha3: 'KIR',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  {
    name: "Korea (Democratic People's Republic of)",
    alpha2: 'KP',
    alpha3: 'PRK',
    region: 'Asia',
    subregion: 'Eastern Asia',
    supportedIdentityReport: false,
  },
  {
    name: 'Korea (Republic of)',
    alpha2: 'KR',
    alpha3: 'KOR',
    region: 'Asia',
    subregion: 'Eastern Asia',
  },
  { name: 'Kuwait', alpha2: 'KW', alpha3: 'KWT', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Kyrgyzstan', alpha2: 'KG', alpha3: 'KGZ', region: 'Asia', subregion: 'Central Asia' },
  {
    name: "Lao People's Democratic Republic",
    alpha2: 'LA',
    alpha3: 'LAO',
    region: 'Asia',
    subregion: 'South-Eastern Asia',
    supportedIdentityReport: false,
  },
  { name: 'Latvia', alpha2: 'LV', alpha3: 'LVA', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Lebanon', alpha2: 'LB', alpha3: 'LBN', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Lesotho', alpha2: 'LS', alpha3: 'LSO', region: 'Africa', subregion: 'Southern Africa' },
  {
    name: 'Liberia',
    alpha2: 'LR',
    alpha3: 'LBR',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Libya',
    alpha2: 'LY',
    alpha3: 'LBY',
    region: 'Africa',
    subregion: 'Northern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Liechtenstein',
    alpha2: 'LI',
    alpha3: 'LIE',
    region: 'Europe',
    subregion: 'Western Europe',
  },
  { name: 'Lithuania', alpha2: 'LT', alpha3: 'LTU', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Luxembourg', alpha2: 'LU', alpha3: 'LUX', region: 'Europe', subregion: 'Western Europe' },
  {
    name: 'Macao',
    alpha2: 'MO',
    alpha3: 'MAC',
    region: 'Asia',
    subregion: 'Eastern Asia',
    supportedIdentityReport: false,
  },
  {
    name: 'Madagascar',
    alpha2: 'MG',
    alpha3: 'MDG',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  { name: 'Malawi', alpha2: 'MW', alpha3: 'MWI', region: 'Africa', subregion: 'Eastern Africa' },
  { name: 'Malaysia', alpha2: 'MY', alpha3: 'MYS', region: 'Asia', subregion: 'South-Eastern Asia' },
  {
    name: 'Maldives',
    alpha2: 'MV',
    alpha3: 'MDV',
    region: 'Asia',
    subregion: 'Southern Asia',
    supportedIdentityReport: false,
  },
  { name: 'Mali', alpha2: 'ML', alpha3: 'MLI', region: 'Africa', subregion: 'Western Africa' },
  { name: 'Malta', alpha2: 'MT', alpha3: 'MLT', region: 'Europe', subregion: 'Southern Europe' },
  {
    name: 'Marshall Islands',
    alpha2: 'MH',
    alpha3: 'MHL',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Martinique',
    alpha2: 'MQ',
    alpha3: 'MTQ',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  { name: 'Mauritania', alpha2: 'MR', alpha3: 'MRT', region: 'Africa', subregion: 'Western Africa' },
  {
    name: 'Mauritius',
    alpha2: 'MU',
    alpha3: 'MUS',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Mayotte',
    alpha2: 'YT',
    alpha3: 'MYT',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  { name: 'Mexico', alpha2: 'MX', alpha3: 'MEX', region: 'Americas', subregion: 'Central America' },
  {
    name: 'Micronesia (Federated States of)',
    alpha2: 'FM',
    alpha3: 'FSM',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Moldova (Republic of)',
    alpha2: 'MD',
    alpha3: 'MDA',
    region: 'Europe',
    subregion: 'Eastern Europe',
  },
  { name: 'Monaco', alpha2: 'MC', alpha3: 'MCO', region: 'Europe', subregion: 'Western Europe' },
  {
    name: 'Mongolia',
    alpha2: 'MN',
    alpha3: 'MNG',
    region: 'Asia',
    subregion: 'Eastern Asia',
    supportedIdentityReport: false,
  },
  { name: 'Montenegro', alpha2: 'ME', alpha3: 'MNE', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Montserrat', alpha2: 'MS', alpha3: 'MSR', region: 'Americas', subregion: 'Caribbean' },
  { name: 'Morocco', alpha2: 'MA', alpha3: 'MAR', region: 'Africa', subregion: 'Northern Africa' },
  {
    name: 'Mozambique',
    alpha2: 'MZ',
    alpha3: 'MOZ',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Myanmar',
    alpha2: 'MM',
    alpha3: 'MMR',
    region: 'Asia',
    subregion: 'South-Eastern Asia',
    supportedIdentityReport: false,
  },
  { name: 'Namibia', alpha2: 'NA', alpha3: 'NAM', region: 'Africa', subregion: 'Southern Africa' },
  {
    name: 'Nauru',
    alpha2: 'NR',
    alpha3: 'NRU',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  { name: 'Nepal', alpha2: 'NP', alpha3: 'NPL', region: 'Asia', subregion: 'Southern Asia' },
  { name: 'Netherlands', alpha2: 'NL', alpha3: 'NLD', region: 'Europe', subregion: 'Western Europe' },
  {
    name: 'New Caledonia',
    alpha2: 'NC',
    alpha3: 'NCL',
    region: 'Oceania',
    subregion: 'Melanesia',
    supportedIdentityReport: false,
  },
  {
    name: 'New Zealand',
    alpha2: 'NZ',
    alpha3: 'NZL',
    region: 'Oceania',
    subregion: 'Australia and New Zealand',
  },
  { name: 'Nicaragua', alpha2: 'NI', alpha3: 'NIC', region: 'Americas', subregion: 'Central America' },
  { name: 'Niger', alpha2: 'NE', alpha3: 'NER', region: 'Africa', subregion: 'Western Africa' },
  { name: 'Nigeria', alpha2: 'NG', alpha3: 'NGA', region: 'Africa', subregion: 'Western Africa' },
  {
    name: 'Niue',
    alpha2: 'NU',
    alpha3: 'NIU',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Norfolk Island',
    alpha2: 'NF',
    alpha3: 'NFK',
    region: 'Oceania',
    subregion: 'Australia and New Zealand',
  },
  {
    name: 'North Macedonia',
    alpha2: 'MK',
    alpha3: 'MKD',
    region: 'Europe',
    subregion: 'Southern Europe',
    supportedIdentityReport: false,
  },
  {
    name: 'Northern Mariana Islands',
    alpha2: 'MP',
    alpha3: 'MNP',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  { name: 'Norway', alpha2: 'NO', alpha3: 'NOR', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Oman', alpha2: 'OM', alpha3: 'OMN', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Pakistan', alpha2: 'PK', alpha3: 'PAK', region: 'Asia', subregion: 'Southern Asia' },
  {
    name: 'Palau',
    alpha2: 'PW',
    alpha3: 'PLW',
    region: 'Oceania',
    subregion: 'Micronesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Palestine, State of',
    alpha2: 'PS',
    alpha3: 'PSE',
    region: 'Asia',
    subregion: 'Western Asia',
  },
  { name: 'Panama', alpha2: 'PA', alpha3: 'PAN', region: 'Americas', subregion: 'Central America' },
  {
    name: 'Papua New Guinea',
    alpha2: 'PG',
    alpha3: 'PNG',
    region: 'Oceania',
    subregion: 'Melanesia',
    supportedIdentityReport: false,
  },
  { name: 'Paraguay', alpha2: 'PY', alpha3: 'PRY', region: 'Americas', subregion: 'South America' },
  { name: 'Peru', alpha2: 'PE', alpha3: 'PER', region: 'Americas', subregion: 'South America' },
  {
    name: 'Philippines',
    alpha2: 'PH',
    alpha3: 'PHL',
    region: 'Asia',
    subregion: 'South-Eastern Asia',
  },
  {
    name: 'Pitcairn',
    alpha2: 'PN',
    alpha3: 'PCN',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  { name: 'Poland', alpha2: 'PL', alpha3: 'POL', region: 'Europe', subregion: 'Eastern Europe' },
  { name: 'Portugal', alpha2: 'PT', alpha3: 'PRT', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Puerto Rico', alpha2: 'PR', alpha3: 'PRI', region: 'Americas', subregion: 'Caribbean' },
  { name: 'Qatar', alpha2: 'QA', alpha3: 'QAT', region: 'Asia', subregion: 'Western Asia' },
  {
    name: 'Republic of Kosovo',
    alpha2: 'XK',
    alpha3: 'RKS',
    region: 'Europe',
    subregion: 'Southern Europe',
  },
  { name: 'Romania', alpha2: 'RO', alpha3: 'ROU', region: 'Europe', subregion: 'Eastern Europe' },
  {
    name: 'Russian Federation',
    alpha2: 'RU',
    alpha3: 'RUS',
    region: 'Europe',
    subregion: 'Eastern Europe',
  },
  {
    name: 'Rwanda',
    alpha2: 'RW',
    alpha3: 'RWA',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Réunion',
    alpha2: 'RE',
    alpha3: 'REU',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Saint Barthélemy',
    alpha2: 'BL',
    alpha3: 'BLM',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  {
    name: 'Saint Helena, Ascension and Tristan da Cunha',
    alpha2: 'SH',
    alpha3: 'SHN',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Saint Kitts and Nevis',
    alpha2: 'KN',
    alpha3: 'KNA',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  { name: 'Saint Lucia', alpha2: 'LC', alpha3: 'LCA', region: 'Americas', subregion: 'Caribbean' },
  {
    name: 'Saint Martin (French part)',
    alpha2: 'MF',
    alpha3: 'MAF',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  {
    name: 'Saint Pierre and Miquelon',
    alpha2: 'PM',
    alpha3: 'SPM',
    region: 'Americas',
    subregion: 'Northern America',
    supportedIdentityReport: false,
  },
  {
    name: 'Saint Vincent and the Grenadines',
    alpha2: 'VC',
    alpha3: 'VCT',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  { name: 'Samoa', alpha2: 'WS', alpha3: 'WSM', region: 'Oceania', subregion: 'Polynesia' },
  {
    name: 'San Marino',
    alpha2: 'SM',
    alpha3: 'SMR',
    region: 'Europe',
    subregion: 'Southern Europe',
    supportedIdentityReport: false,
  },
  {
    name: 'Sao Tome and Principe',
    alpha2: 'ST',
    alpha3: 'STP',
    region: 'Africa',
    subregion: 'Middle Africa',
    supportedIdentityReport: false,
  },
  { name: 'Saudi Arabia', alpha2: 'SA', alpha3: 'SAU', region: 'Asia', subregion: 'Western Asia' },
  {
    name: 'Senegal',
    alpha2: 'SN',
    alpha3: 'SEN',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  { name: 'Serbia', alpha2: 'RS', alpha3: 'SRB', region: 'Europe', subregion: 'Southern Europe' },
  {
    name: 'Seychelles',
    alpha2: 'SC',
    alpha3: 'SYC',
    region: 'Africa',
    subregion: 'Eastern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Sierra Leone',
    alpha2: 'SL',
    alpha3: 'SLE',
    region: 'Africa',
    subregion: 'Western Africa',
    supportedIdentityReport: false,
  },
  { name: 'Singapore', alpha2: 'SG', alpha3: 'SGP', region: 'Asia', subregion: 'South-Eastern Asia' },
  {
    name: 'Sint Maarten (Dutch part)',
    alpha2: 'SX',
    alpha3: 'SXM',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  { name: 'Slovakia', alpha2: 'SK', alpha3: 'SVK', region: 'Europe', subregion: 'Eastern Europe' },
  { name: 'Slovenia', alpha2: 'SI', alpha3: 'SVN', region: 'Europe', subregion: 'Southern Europe' },
  {
    name: 'Solomon Islands',
    alpha2: 'SB',
    alpha3: 'SLB',
    region: 'Oceania',
    subregion: 'Melanesia',
    supportedIdentityReport: false,
  },
  { name: 'Somalia', alpha2: 'SO', alpha3: 'SOM', region: 'Africa', subregion: 'Eastern Africa' },
  {
    name: 'South Africa',
    alpha2: 'ZA',
    alpha3: 'ZAF',
    region: 'Africa',
    subregion: 'Southern Africa',
  },
  {
    name: 'South Georgia and the South Sandwich Islands',
    alpha2: 'GS',
    alpha3: 'SGS',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  {
    name: 'South Sudan',
    alpha2: 'SS',
    alpha3: 'SSD',
    region: 'Africa',
    subregion: 'Northern Africa',
    supportedIdentityReport: false,
  },
  { name: 'Spain', alpha2: 'ES', alpha3: 'ESP', region: 'Europe', subregion: 'Southern Europe' },
  { name: 'Sri Lanka', alpha2: 'LK', alpha3: 'LKA', region: 'Asia', subregion: 'Southern Asia' },
  { name: 'Sudan', alpha2: 'SD', alpha3: 'SDN', region: 'Africa', subregion: 'Northern Africa' },
  {
    name: 'Suriname',
    alpha2: 'SR',
    alpha3: 'SUR',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  {
    name: 'Svalbard and Jan Mayen',
    alpha2: 'SJ',
    alpha3: 'SJM',
    region: 'Europe',
    subregion: 'Northern Europe',
    supportedIdentityReport: false,
  },
  { name: 'Sweden', alpha2: 'SE', alpha3: 'SWE', region: 'Europe', subregion: 'Northern Europe' },
  { name: 'Switzerland', alpha2: 'CH', alpha3: 'CHE', region: 'Europe', subregion: 'Western Europe' },
  {
    name: 'Syrian Arab Republic',
    alpha2: 'SY',
    alpha3: 'SYR',
    region: 'Asia',
    subregion: 'Western Asia',
  },
  {
    name: 'Taiwan, Province of China',
    alpha2: 'TW',
    alpha3: 'TWN',
    region: 'Asia',
    subregion: 'Eastern Asia',
  },
  { name: 'Tajikistan', alpha2: 'TJ', alpha3: 'TJK', region: 'Asia', subregion: 'Central Asia' },
  {
    name: 'Tanzania, United Republic of',
    alpha2: 'TZ',
    alpha3: 'TZA',
    region: 'Africa',
    subregion: 'Eastern Africa',
  },
  { name: 'Thailand', alpha2: 'TH', alpha3: 'THA', region: 'Asia', subregion: 'South-Eastern Asia' },
  {
    name: 'Timor-Leste',
    alpha2: 'TL',
    alpha3: 'TLS',
    region: 'Asia',
    subregion: 'South-Eastern Asia',
    supportedIdentityReport: false,
  },
  { name: 'Togo', alpha2: 'TG', alpha3: 'TGO', region: 'Africa', subregion: 'Western Africa' },
  {
    name: 'Tokelau',
    alpha2: 'TK',
    alpha3: 'TKL',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Tonga',
    alpha2: 'TO',
    alpha3: 'TON',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Trinidad and Tobago',
    alpha2: 'TT',
    alpha3: 'TTO',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  { name: 'Tunisia', alpha2: 'TN', alpha3: 'TUN', region: 'Africa', subregion: 'Northern Africa' },
  { name: 'Turkey', alpha2: 'TR', alpha3: 'TUR', region: 'Asia', subregion: 'Western Asia' },
  { name: 'Turkmenistan', alpha2: 'TM', alpha3: 'TKM', region: 'Asia', subregion: 'Central Asia' },
  {
    name: 'Turks and Caicos Islands',
    alpha2: 'TC',
    alpha3: 'TCA',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  { name: 'Tuvalu', alpha2: 'TV', alpha3: 'TUV', region: 'Oceania', subregion: 'Polynesia' },
  { name: 'Uganda', alpha2: 'UG', alpha3: 'UGA', region: 'Africa', subregion: 'Eastern Africa' },
  { name: 'Ukraine', alpha2: 'UA', alpha3: 'UKR', region: 'Europe', subregion: 'Eastern Europe' },
  {
    name: 'United Arab Emirates',
    alpha2: 'AE',
    alpha3: 'ARE',
    region: 'Asia',
    subregion: 'Western Asia',
  },
  {
    name: 'United Kingdom',
    alpha2: 'GB',
    alpha3: 'GBR',
    region: 'Europe',
    subregion: 'Northern Europe',
  },
  {
    name: 'United States Minor Outlying Islands',
    alpha2: 'UM',
    alpha3: 'UMI',
    region: 'Americas',
    subregion: 'Northern America',
    supportedIdentityReport: false,
  },
  {
    name: 'United States of America',
    alpha2: 'US',
    alpha3: 'USA',
    region: 'Americas',
    subregion: 'Northern America',
  },
  {
    name: 'Uruguay',
    alpha2: 'UY',
    alpha3: 'URY',
    region: 'Americas',
    subregion: 'South America',
    supportedIdentityReport: false,
  },
  { name: 'Uzbekistan', alpha2: 'UZ', alpha3: 'UZB', region: 'Asia', subregion: 'Central Asia' },
  { name: 'Vanuatu', alpha2: 'VU', alpha3: 'VUT', region: 'Oceania', subregion: 'Melanesia' },
  {
    name: 'Venezuela (Bolivarian Republic of)',
    alpha2: 'VE',
    alpha3: 'VEN',
    region: 'Americas',
    subregion: 'South America',
  },
  { name: 'Viet Nam', alpha2: 'VN', alpha3: 'VNM', region: 'Asia', subregion: 'South-Eastern Asia' },
  {
    name: 'Virgin Islands (British)',
    alpha2: 'VG',
    alpha3: 'VGB',
    region: 'Americas',
    subregion: 'Caribbean',
  },
  {
    name: 'Virgin Islands (U.S.)',
    alpha2: 'VI',
    alpha3: 'VIR',
    region: 'Americas',
    subregion: 'Caribbean',
    supportedIdentityReport: false,
  },
  {
    name: 'Wallis and Futuna',
    alpha2: 'WF',
    alpha3: 'WLF',
    region: 'Oceania',
    subregion: 'Polynesia',
    supportedIdentityReport: false,
  },
  {
    name: 'Western Sahara',
    alpha2: 'EH',
    alpha3: 'ESH',
    region: 'Africa',
    subregion: 'Northern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Yemen',
    alpha2: 'YE',
    alpha3: 'YEM',
    region: 'Asia',
    subregion: 'Western Asia',
    supportedIdentityReport: false,
  },
  { name: 'Zambia', alpha2: 'ZM', alpha3: 'ZMB', region: 'Africa', subregion: 'Eastern Africa' },
  { name: 'Zimbabwe', alpha2: 'ZW', alpha3: 'ZWE', region: 'Africa', subregion: 'Eastern Africa' },
  {
    name: 'eSwatini',
    alpha2: 'SZ',
    alpha3: 'SWZ',
    region: 'Africa',
    subregion: 'Southern Africa',
    supportedIdentityReport: false,
  },
  {
    name: 'Åland Islands',
    alpha2: 'AX',
    alpha3: 'ALA',
    region: 'Europe',
    subregion: 'Northern Europe',
  },
] as const;

export type CountryCode = (typeof countries)[number]['alpha3'];
export type Country = Pick<(typeof countries)[number], 'name' | 'alpha3'>;

export default countries;
